import { ASSIGNMENT_TYPES } from './assignment-types'
import { LANGUAGES } from './languages'

export const PermissionLevels = {
    MBA_ONLY: 'mba_only',
    EDITOR: 'editor',
    VIEWER: 'viewer',
    OWNER: 'owner',
}

export const getPermissionLevelName = (permissionLevel, defaultName = 'Unknown') => {
    switch (permissionLevel) {
        case PermissionLevels.OWNER:
            return 'Owner'
        case PermissionLevels.VIEWER:
            return 'Viewer'
        case PermissionLevels.EDITOR:
            return 'Editor'
        case PermissionLevels.MBA_ONLY:
            return 'MBA Only'
        default:
            return defaultName
    }
}

export const SharingLevels = {
    NO_ACCESS: -1,
    MBA_ONLY: 1,
    VIEWABLE: 2,
    FULL_ACCESS: 4,
    OWNER: 5,
    SUPERVISOR: 6,
    REVIEWER: 7,
}

export const sharingLevelWeights = {
    [SharingLevels.NO_ACCESS]: 0,
    [SharingLevels.MBA_ONLY]: 1,
    [SharingLevels.VIEWABLE]: 2,
    [SharingLevels.REVIEWER]: 3,
    [SharingLevels.SUPERVISOR]: 4,
    [SharingLevels.FULL_ACCESS]: 4,
    [SharingLevels.OWNER]: 10,
}

export const getSharingLevelName = (sharingLevel, defaultName = 'Unknown') => {
    switch (sharingLevel) {
        case SharingLevels.NO_ACCESS:
            return 'No Access'
        case SharingLevels.MBA_ONLY:
            return 'MBA Only'
        case SharingLevels.VIEWABLE:
            return 'Viewable'
        case SharingLevels.FULL_ACCESS:
            return 'Full Access'
        case SharingLevels.OWNER:
            return 'Owner'
        case SharingLevels.SUPERVISOR:
            return 'Supervisor'
        case SharingLevels.REVIEWER:
            return 'Reviewer'
        default:
            return defaultName
    }
}

export const ASSIGNMENT_SHARING_LEVELS = {
    NO_ACCESS: {
        value: -1,
        label: 'No Access',
        description: 'Provides no access to this assignment.',
    },
    MBA_ONLY: {
        value: 1,
        label: 'MBA Only',
        description: 'Provides the ability to create MBAs from this assignment.',
    },
    VIEWABLE: {
        value: 2,
        label: 'Viewable',
        description: 'Provides the ability to view this assignment and to create MBAs from it.',
    },
    FULL_ACCESS: {
        value: 4,
        label: 'Full Access',
        description: 'Provides the full access to this assignment.',
    },
    OWNER: {
        value: 5,
        label: 'Co-Owner',
        description: 'Provides owner access to this assignment.',
        languages: [LANGUAGES.NEW_TEXT.value],
        assignmentTypes: [ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value],
    },
    SUPERVISOR: {
        value: 6,
        label: 'Supervisor',
        description: 'Provides the full view access and the ability to review all submissions.',
        languages: [LANGUAGES.NEW_TEXT.value],
        assignmentTypes: [ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value],
    },
    REVIEWER: {
        value: 7,
        label: 'Reviewer',
        description: 'Provides the ability to review own submissions.',
        languages: [LANGUAGES.NEW_TEXT.value],
        assignmentTypes: [ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value],
    },
}

export const filterByAssignmentType = (sharingLevels, currentAssignmentType) => {
    return sharingLevels.filter(
        level =>
            !level.assignmentTypes ||
            level.assignmentTypes.length === 0 ||
            level.assignmentTypes.includes(currentAssignmentType),
    )
}

export const filterOutNoAccessLevel = sharingLevels =>
    sharingLevels.filter(level => level.value > ASSIGNMENT_SHARING_LEVELS.NO_ACCESS.value)

export const filterByLanguage = (sharingLevels, currentLanguage) => {
    return sharingLevels.filter(level => {
        if (level.languages) {
            return level.languages.includes(currentLanguage)
        }

        if (level.excludeLanguages) {
            return !level.excludeLanguages.includes(currentLanguage)
        }

        return true
    })
}

export const filterHigherAccessLevels = (sharingLevels, currentLevel) => {
    if (currentLevel === SharingLevels.OWNER || !currentLevel) return sharingLevels
    return sharingLevels.filter(level => sharingLevelWeights[level.value] < sharingLevelWeights[currentLevel])
}

export const disableHigherAccessLevels = (sharingLevels, currentLevel) => {
    if (currentLevel === SharingLevels.OWNER || !currentLevel) return sharingLevels

    return sharingLevels.map(level => ({
        ...level,
        disabled: sharingLevelWeights[level.value] >= sharingLevelWeights[currentLevel],
    }))
}

export const Perm = {
    CAN_READ: 'can-read',
    CAN_CREATE_MBA: 'can-create-mba',
    CAN_CLONE: 'can-clone',
    CAN_EXPORT: 'can-export',
    CAN_EDIT: 'can-edit',
    CAN_EDIT_FEEDBACK: 'can-edit-feedback',
    CAN_SHARE: 'can-share',
    CAN_DELETE: 'can-delete',
    CAN_REVIEW: 'can-review',
    CAN_DISTRIBUTE_WORKLOAD: 'can-distribute-workload',
    CAN_VIEW_DISTRIBUTE_WORKLOAD: 'can-view-distribute-workload',
    CAN_EDIT_UNASSIGNED: 'can-edit-unassigned',
    CAN_EDIT_ASSIGNED_TO_OTHERS: 'can-edit-assigned-to-others',
    CAN_VIEW_IF_SETTINGS: 'can-view-if-settings',
    CAN_EDIT_IF_SETTINGS: 'can-edit-if-settings',
}

export const ASSIGNMENT_SHARING_LEVELS_TO_PERMISSION_SCOPES = {
    [ASSIGNMENT_SHARING_LEVELS.NO_ACCESS.value]: [],
    [ASSIGNMENT_SHARING_LEVELS.MBA_ONLY.value]: [Perm.CAN_CREATE_MBA],
    [ASSIGNMENT_SHARING_LEVELS.VIEWABLE.value]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_REVIEW,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
    ],
    [ASSIGNMENT_SHARING_LEVELS.FULL_ACCESS.value]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_EDIT,
        Perm.CAN_SHARE,
        Perm.CAN_REVIEW,
        Perm.CAN_VIEW_IF_SETTINGS,
        Perm.CAN_EDIT_IF_SETTINGS,
    ],
    [ASSIGNMENT_SHARING_LEVELS.SUPERVISOR.value]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_EDIT,
        Perm.CAN_REVIEW,
        Perm.CAN_VIEW_IF_SETTINGS,
    ],
    [ASSIGNMENT_SHARING_LEVELS.REVIEWER.value]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_REVIEW,
    ],
    [ASSIGNMENT_SHARING_LEVELS.OWNER.value]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_EDIT,
        Perm.CAN_SHARE,
        Perm.CAN_REVIEW,
        Perm.CAN_DISTRIBUTE_WORKLOAD,
        Perm.CAN_EDIT_UNASSIGNED,
        Perm.CAN_EDIT_ASSIGNED_TO_OTHERS,
        Perm.CAN_VIEW_IF_SETTINGS,
        Perm.CAN_EDIT_IF_SETTINGS,
    ],
}

export const PERMISSION_LEVEL_TO_PERMISSION_SCOPES = {
    [PermissionLevels.MBA_ONLY]: [
        Perm.CAN_CREATE_MBA,
        Perm.CAN_VIEW_IF_SETTINGS,
    ],
    [PermissionLevels.VIEWER]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_REVIEW,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_VIEW_DISTRIBUTE_WORKLOAD,
        Perm.CAN_VIEW_IF_SETTINGS,
    ],
    [PermissionLevels.EDITOR]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_EDIT,
        Perm.CAN_SHARE,
        Perm.CAN_REVIEW,
        Perm.CAN_VIEW_DISTRIBUTE_WORKLOAD,
        Perm.CAN_VIEW_IF_SETTINGS,
        Perm.CAN_EDIT_IF_SETTINGS,
    ],
    [PermissionLevels.OWNER]: [
        Perm.CAN_READ,
        Perm.CAN_CREATE_MBA,
        Perm.CAN_CLONE,
        Perm.CAN_EXPORT,
        Perm.CAN_EDIT,
        Perm.CAN_SHARE,
        Perm.CAN_DELETE,
        Perm.CAN_REVIEW,
        Perm.CAN_DISTRIBUTE_WORKLOAD,
        Perm.CAN_VIEW_DISTRIBUTE_WORKLOAD,
        Perm.CAN_EDIT_UNASSIGNED,
        Perm.CAN_EDIT_ASSIGNED_TO_OTHERS,
        Perm.CAN_EDIT_FEEDBACK,
        Perm.CAN_VIEW_IF_SETTINGS,
        Perm.CAN_EDIT_IF_SETTINGS,
    ],
}

export const State = {
    NOT_MBA: 'not-mba',
    LATEST: 'latest',
    UNPUBLISHED: 'unpublished',
    NOT_PROCESSING: 'not-processing',
    NO_SUBMISSIONS: 'no-submissions',
}

export const STATE_ERROR_MESSAGES = {
    [State.NOT_MBA]: 'Action not available for MBA assignments',
    [State.LATEST]: 'Can only edit latest version',
    [State.UNPUBLISHED]: 'Cannot edit published versions',
    [State.NOT_PROCESSING]: 'Cannot edit while processing',
    [State.NO_SUBMISSIONS]: 'Cannot edit after submissions have been added',
}
